<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import {
  useFieldArray,
  useValidateForm,
  Field,
  ErrorMessage
} from 'vee-validate'
import { useCartStore } from '../stores/cartStore'
import CountrySelect from './FormUIComponents/CountryRegionSelect/CountrySelect.vue'
import RegionSelect from './FormUIComponents/CountryRegionSelect/RegionSelect.vue'
import RadioButtons from './FormUIComponents/RadioButtons.vue'
import regions from './FormUIComponents/CountryRegionSelect/data.js'

const props = defineProps({
  formMetadata: {
    type: Object,
    required: true
  },
  fieldSet: {
    type: String,
    required: true
  }
})

const store = useCartStore()
const validate = useValidateForm()

const { fields, push, remove, update } = useFieldArray(props.fieldSet)

// Track which country is selected for each officer to control region display
const selectedCountries = ref({})

// Initialize selectedCountries and name fields from existing field values
onMounted(() => {
  fields.value.forEach((field, idx) => {
    // Initialize country values
    if (field.value && field.value.country) {
      selectedCountries.value[idx] = field.value.country

      // Ensure the hidden field is updated with the correct value
      const countryFieldName = getFieldName(idx, 'country')
      const countryField = document.querySelector(
        `[name='${countryFieldName}']`
      )
      if (countryField) {
        countryField.value = field.value.country
      }

      // Ensure the state field is updated with the correct value
      if (field.value.state) {
        const stateFieldName = getFieldName(idx, 'state')
        const stateField = document.querySelector(`[name='${stateFieldName}']`)
        if (stateField) {
          stateField.value = field.value.state
        }
      }
    } else {
      selectedCountries.value[idx] = 'US' // Default to US

      // If country is not set, initialize it to US in the field value
      if (field.value) {
        field.value.country = 'US'

        // Update the hidden field
        const countryFieldName = getFieldName(idx, 'country')
        const countryField = document.querySelector(
          `[name='${countryFieldName}']`
        )
        if (countryField) {
          countryField.value = 'US'
        }
      }
    }

    // Split full name into first/middle/last name for person officers if not already set
    if (
      field.value &&
      field.value.officer_type === 'P' &&
      field.value.name &&
      (!field.value.first_name || !field.value.last_name)
    ) {
      const nameParts = field.value.name.split(' ')
      if (nameParts.length >= 3) {
        field.value.first_name = nameParts[0]
        field.value.middle_name = nameParts.slice(1, -1).join(' ')
        field.value.last_name = nameParts[nameParts.length - 1]
      } else if (nameParts.length === 2) {
        field.value.first_name = nameParts[0]
        field.value.middle_name = ''
        field.value.last_name = nameParts[1]
      } else {
        field.value.first_name = field.value.name
        field.value.middle_name = ''
        field.value.last_name = ''
      }
    }
  })
})

// Officer type choices
const officerTypeChoices = [
  { id: 'P', name: 'Person' },
  { id: 'C', name: 'Corporation' }
]

const toggleEditMode = async (id) => {
  const isValid = await validateFieldSet(id)
  if (fields.value[id].editMode) {
    if (isValid) {
      // add a new officer if the id is null
      if (fields.value[id].value.id === null) {
        store.clearError()
        store.setIsLoading(true)
        try {
          const newOfficer = await store.createOfficer(fields.value[id].value)
          update(id, newOfficer)
        } catch (error) {
          console.error('Failed to create officer:', error)
          store.setError('Failed to create officer. Please try again.')
        } finally {
          store.setIsLoading(false)
        }
      }

      fields.value[id].editMode = false
    }
  } else {
    fields.value[id].editMode = true
  }
}

// Update full name when first, middle or last name changes
const updateFullName = (idx) => {
  if (
    fields.value[idx].value.officer_type === 'P' &&
    fields.value[idx].value.first_name !== undefined &&
    fields.value[idx].value.last_name !== undefined
  ) {
    const firstName = fields.value[idx].value.first_name?.trim() || ''
    const middleName = fields.value[idx].value.middle_name?.trim() || ''
    const lastName = fields.value[idx].value.last_name?.trim() || ''

    const fullName = [firstName, middleName, lastName].filter(Boolean).join(' ')
    fields.value[idx].value.name = fullName

    // Also update the hidden name field if it exists
    const nameFieldName = getFieldName(idx, 'name')
    const nameField = document.querySelector(`[name='${nameFieldName}']`)
    if (nameField) {
      nameField.value = fullName
    }
  }
}

// Handle officer type change
const onOfficerTypeChange = (idx, type) => {
  fields.value[idx].value.officer_type = type

  // If switching to Person type, initialize first/middle/last name from full name
  if (type === 'P' && fields.value[idx].value.name) {
    const nameParts = fields.value[idx].value.name.split(' ')
    if (nameParts.length >= 3) {
      fields.value[idx].value.first_name = nameParts[0]
      fields.value[idx].value.middle_name = nameParts.slice(1, -1).join(' ')
      fields.value[idx].value.last_name = nameParts[nameParts.length - 1]
    } else if (nameParts.length === 2) {
      fields.value[idx].value.first_name = nameParts[0]
      fields.value[idx].value.middle_name = ''
      fields.value[idx].value.last_name = nameParts[1]
    } else {
      fields.value[idx].value.first_name = fields.value[idx].value.name
      fields.value[idx].value.middle_name = ''
      fields.value[idx].value.last_name = ''
    }
  }
}

const validateFieldSet = async (id) => {
  const formValidationDetails = await validate()
  const formSetFields = store.getFormsetFields(
    props.formMetadata,
    props.fieldSet,
    id
  )
  if (!formValidationDetails.valid) {
    for (const field of formSetFields) {
      try {
        if (formValidationDetails.errors[field.name]) {
          return false // Return false as soon as the first field is invalid
        }
      } catch (error) {
        console.error(error)
      }
    }
  }

  return true // All fields are valid
}

const addForm = () => {
  push({
    id: null,
    name: null,
    first_name: '',
    middle_name: '',
    last_name: '',
    officer_type: 'P', // Default to Person
    country: 'US', // Default to US
    state: '', // Initialize state as empty
    editMode: true
  })

  // Set the country for the new officer
  const newIndex = fields.value.length - 1
  selectedCountries.value[newIndex] = 'US'

  // Ensure the hidden fields are updated after the DOM has updated
  setTimeout(() => {
    // Update country field
    const countryFieldName = getFieldName(newIndex, 'country')
    const countryField = document.querySelector(`[name='${countryFieldName}']`)
    if (countryField) {
      countryField.value = 'US'
    }

    // Initialize state field as empty
    const stateFieldName = getFieldName(newIndex, 'state')
    const stateField = document.querySelector(`[name='${stateFieldName}']`)
    if (stateField) {
      stateField.value = ''
    }
  }, 0)

  fields.value[newIndex].editMode = true
}

// Handle country change
const onCountryChange = (idx, country) => {
  selectedCountries.value[idx] = country

  // Update the reactive state in the parent form
  if (fields.value[idx] && fields.value[idx].value) {
    fields.value[idx].value.country = country

    // Reset the state when country changes
    fields.value[idx].value.state = ''
  }

  // Also update the field value to ensure it's saved
  const fieldName = getFieldName(idx, 'country')
  const field = document.querySelector(`[name='${fieldName}']`)
  if (field) {
    field.value = country

    // Reset the state field when country changes
    const stateFieldName = getFieldName(idx, 'state')
    const stateField = document.querySelector(`[name='${stateFieldName}']`)
    if (stateField) {
      stateField.value = ''
    }
  }
}

// Handle region change
const onRegionChange = (idx, region) => {
  // Update the reactive state in the parent form
  if (fields.value[idx] && fields.value[idx].value) {
    fields.value[idx].value.state = region
  }

  // Update the field value
  const fieldName = getFieldName(idx, 'state')
  const field = document.querySelector(`[name='${fieldName}']`)
  if (field) {
    field.value = region
  }
}

// Get field name with proper fieldset prefix
const getFieldName = (idx, fieldName) => {
  return `${props.fieldSet}[${idx}].${fieldName}`
}

// Get validation rules for a field
const getValidators = (fieldName, required = false) => {
  const rules = {}

  if (required) {
    rules.required = true
  }

  if (fieldName.includes('name')) {
    rules.max = 200
  }

  if (fieldName.includes('address_1')) {
    rules.max = 120
  }

  if (fieldName.includes('address_2')) {
    rules.max = 270
  }

  if (fieldName.includes('city')) {
    rules.max = 50
  }

  if (fieldName.includes('zip')) {
    rules.max = 20
  }

  return rules
}

// Display the officer data in read-only mode
const getOfficerDisplayData = (officer) => {
  const data = []

  // We'll handle name, type, and title separately in the template

  // Format the address in a more structured way
  const addressLines = []
  if (officer.address_1) {
    addressLines.push(officer.address_1)
  }

  if (officer.address_2) {
    addressLines.push(officer.address_2)
  }

  // City, State ZIP
  const cityStateZip = []
  if (officer.city) cityStateZip.push(officer.city)
  if (officer.state) cityStateZip.push(officer.state)
  if (officer.zip) cityStateZip.push(officer.zip)

  if (cityStateZip.length > 0) {
    addressLines.push(cityStateZip.join(', '))
  }

  // Country
  if (officer.country) {
    const countryName = getCountryName(officer.country)
    if (countryName && countryName !== 'United States') {
      addressLines.push(countryName)
    }
  }

  if (addressLines.length > 0) {
    data.push({
      label: 'Address',
      value: addressLines.join('<br>'),
      isHtml: true
    })
  }

  return data
}

// Get country name from country code
const getCountryName = (countryCode) => {
  try {
    const country = regions.find((r) => r.countryShortCode === countryCode)
    return country ? country.countryName : countryCode
  } catch (error) {
    console.error('Error getting country name:', error)
    return countryCode
  }
}

// Get officer type display name
const getOfficerTypeName = (officerType) => {
  const typeObj = officerTypeChoices.find((t) => t.id === officerType)
  return typeObj ? typeObj.name : officerType
}

// Get display name for officer based on officer type
const getOfficerDisplayName = (officer) => {
  if (!officer) return 'Officer'

  if (officer.officer_type === 'P') {
    const parts = []
    if (officer.first_name) parts.push(officer.first_name)
    if (officer.middle_name) parts.push(officer.middle_name)
    if (officer.last_name) parts.push(officer.last_name)

    if (parts.length > 0) {
      return parts.join(' ')
    }
  }

  return officer.name || 'Officer'
}

// Watch for changes in fields to update selectedCountries
watch(
  fields,
  (newFields) => {
    newFields.forEach((field, idx) => {
      if (field.value && field.value.country && !selectedCountries.value[idx]) {
        selectedCountries.value[idx] = field.value.country
      }
    })
  },
  { deep: true }
)
</script>

<template>
  <div class="row gx-3 gy-2 align-items-center">
    <div
      class="card shadow-none border mb-3"
      v-for="(entry, idx) in fields"
      :key="idx"
    >
      <div class="card-body p-3">
        <!-- Edit Mode -->
        <div v-show="entry.editMode">
          <h5 class="card-title mb-4">
            {{ entry.value?.id ? 'Edit Officer' : 'Add New Officer' }}
          </h5>

          <div class="row mb-4">
            <!-- Officer Type -->
            <div class="col-md-6 mb-3">
              <label class="form-label fw-semibold">Officer Type</label>
              <Field
                as="select"
                :id="getFieldName(idx, 'officer_type')"
                :name="getFieldName(idx, 'officer_type')"
                :rules="getValidators('officer_type', true)"
                class="form-select"
                @change="(e) => onOfficerTypeChange(idx, e.target.value)"
              >
                <option value="" disabled>Select Officer Type</option>
                <option
                  v-for="type in officerTypeChoices"
                  :key="type.id"
                  :value="type.id"
                >
                  {{ type.name }}
                </option>
              </Field>
              <ErrorMessage
                :name="getFieldName(idx, 'officer_type')"
                v-slot="{ message }"
              >
                <p class="text-danger small mt-1">{{ message }}</p>
              </ErrorMessage>
            </div>

            <!-- Officer Title -->
            <div class="col-md-6 mb-3">
              <label class="form-label fw-semibold">Officer Title</label>
              <Field
                type="text"
                :id="getFieldName(idx, 'title')"
                :name="getFieldName(idx, 'title')"
                :rules="getValidators('title', true)"
                class="form-control"
                placeholder="Enter Officer Title (e.g., President, Director)"
              />
              <ErrorMessage
                :name="getFieldName(idx, 'title')"
                v-slot="{ message }"
              >
                <p class="text-danger small mt-1">{{ message }}</p>
              </ErrorMessage>
            </div>
          </div>

          <!-- Name Fields -->
          <div class="row mb-4">
            <!-- Different name fields based on officer type -->
            <template v-if="entry.value?.officer_type === 'P'">
              <!-- First Name, Middle Name, and Last Name for Person -->
              <div class="col-md-4 mb-3">
                <label class="form-label fw-semibold">First Name</label>
                <Field
                  type="text"
                  :id="getFieldName(idx, 'first_name')"
                  :name="getFieldName(idx, 'first_name')"
                  :rules="getValidators('first_name', true)"
                  class="form-control"
                  placeholder="First Name"
                  @input="
                    (e) => {
                      entry.value.first_name = e.target.value
                      updateFullName(idx)
                    }
                  "
                />
                <ErrorMessage
                  :name="getFieldName(idx, 'first_name')"
                  v-slot="{ message }"
                >
                  <p class="text-danger small mt-1">{{ message }}</p>
                </ErrorMessage>
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label fw-semibold">Middle Name</label>
                <Field
                  type="text"
                  :id="getFieldName(idx, 'middle_name')"
                  :name="getFieldName(idx, 'middle_name')"
                  :rules="getValidators('middle_name', false)"
                  class="form-control"
                  placeholder="Middle Name"
                  @input="
                    (e) => {
                      entry.value.middle_name = e.target.value
                      updateFullName(idx)
                    }
                  "
                />
                <ErrorMessage
                  :name="getFieldName(idx, 'middle_name')"
                  v-slot="{ message }"
                >
                  <p class="text-danger small mt-1">{{ message }}</p>
                </ErrorMessage>
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label fw-semibold">Last Name</label>
                <Field
                  type="text"
                  :id="getFieldName(idx, 'last_name')"
                  :name="getFieldName(idx, 'last_name')"
                  :rules="getValidators('last_name', true)"
                  class="form-control"
                  placeholder="Last Name"
                  @input="
                    (e) => {
                      entry.value.last_name = e.target.value
                      updateFullName(idx)
                    }
                  "
                />
                <ErrorMessage
                  :name="getFieldName(idx, 'last_name')"
                  v-slot="{ message }"
                >
                  <p class="text-danger small mt-1">{{ message }}</p>
                </ErrorMessage>
              </div>
              <!-- Hidden field for full name -->
              <Field
                type="hidden"
                :id="getFieldName(idx, 'name')"
                :name="getFieldName(idx, 'name')"
                :value="entry.value?.name"
              />
            </template>
            <template v-else>
              <!-- Full Name for Corporation -->
              <div class="col-md-12 mb-3">
                <label class="form-label fw-semibold">Corporation Name</label>
                <Field
                  type="text"
                  :id="getFieldName(idx, 'name')"
                  :name="getFieldName(idx, 'name')"
                  :rules="getValidators('name', true)"
                  class="form-control"
                  placeholder="Corporation Name"
                />
                <ErrorMessage
                  :name="getFieldName(idx, 'name')"
                  v-slot="{ message }"
                >
                  <p class="text-danger small mt-1">{{ message }}</p>
                </ErrorMessage>
              </div>
            </template>
          </div>

          <h6 class="mb-3 text-secondary">Address Information</h6>

          <!-- Address Fields -->
          <div class="row mb-4">
            <div class="col-md-12 mb-3">
              <label class="form-label fw-semibold">Address Line 1</label>
              <Field
                type="text"
                :id="getFieldName(idx, 'address_1')"
                :name="getFieldName(idx, 'address_1')"
                :rules="getValidators('address_1', true)"
                class="form-control"
                placeholder="Street Address"
              />
              <ErrorMessage
                :name="getFieldName(idx, 'address_1')"
                v-slot="{ message }"
              >
                <p class="text-danger small mt-1">{{ message }}</p>
              </ErrorMessage>
            </div>

            <div class="col-md-12 mb-3">
              <label class="form-label fw-semibold">Address Line 2</label>
              <Field
                type="text"
                :id="getFieldName(idx, 'address_2')"
                :name="getFieldName(idx, 'address_2')"
                :rules="getValidators('address_2', false)"
                class="form-control"
                placeholder="Apt, Suite, Building (optional)"
              />
              <ErrorMessage
                :name="getFieldName(idx, 'address_2')"
                v-slot="{ message }"
              >
                <p class="text-danger small mt-1">{{ message }}</p>
              </ErrorMessage>
            </div>
          </div>

          <!-- Country Selection -->
          <div class="row mb-4">
            <div class="col-md-12 mb-3">
              <label class="form-label fw-semibold">Country</label>
              <CountrySelect
                :country="selectedCountries[idx] || 'US'"
                @update:modelValue="(country) => onCountryChange(idx, country)"
                class="form-select"
                topCountry="US"
                :id="getFieldName(idx, 'country')"
              />
              <!-- Hidden field to store the country value for form submission -->
              <Field
                type="hidden"
                :name="getFieldName(idx, 'country')"
                :value="selectedCountries[idx] || 'US'"
                :rules="getValidators('country', true)"
              />
              <ErrorMessage
                :name="getFieldName(idx, 'country')"
                v-slot="{ message }"
              >
                <p class="text-danger small mt-1">{{ message }}</p>
              </ErrorMessage>
            </div>
          </div>

          <!-- City, State/Region, Zip -->
          <div class="row mb-4">
            <div class="col-md-4 mb-3">
              <label class="form-label fw-semibold">City</label>
              <Field
                type="text"
                :id="getFieldName(idx, 'city')"
                :name="getFieldName(idx, 'city')"
                :rules="getValidators('city', true)"
                class="form-control"
                placeholder="City"
              />
              <ErrorMessage
                :name="getFieldName(idx, 'city')"
                v-slot="{ message }"
              >
                <p class="text-danger small mt-1">{{ message }}</p>
              </ErrorMessage>
            </div>

            <div class="col-md-4 mb-3">
              <label class="form-label fw-semibold">State/Region</label>
              <RegionSelect
                :country="selectedCountries[idx] || 'US'"
                :region="entry.value?.state || ''"
                @update:modelValue="(region) => onRegionChange(idx, region)"
                class="form-select"
                :id="getFieldName(idx, 'state') + '_select'"
                placeholder="Select State/Region"
              />
              <!-- Hidden field to store the state value for form submission -->
              <Field
                type="hidden"
                :name="getFieldName(idx, 'state')"
                :rules="getValidators('state', true)"
              />
              <ErrorMessage
                :name="getFieldName(idx, 'state')"
                v-slot="{ message }"
              >
                <p class="text-danger small mt-1">{{ message }}</p>
              </ErrorMessage>
            </div>

            <div class="col-md-4 mb-3">
              <label class="form-label fw-semibold">Postal Code</label>
              <Field
                type="text"
                :id="getFieldName(idx, 'zip')"
                :name="getFieldName(idx, 'zip')"
                :rules="getValidators('zip', true)"
                class="form-control"
                placeholder="Postal Code"
              />
              <ErrorMessage
                :name="getFieldName(idx, 'zip')"
                v-slot="{ message }"
              >
                <p class="text-danger small mt-1">{{ message }}</p>
              </ErrorMessage>
            </div>
          </div>
        </div>

        <!-- View Mode -->
        <div v-show="!entry.editMode" class="officer-display">
          <!-- Officer Information Section -->
          <div class="agent-info mb-4">
            <h4 class="agent-name mb-1">
              <!-- Display name based on officer type -->
              <template v-if="entry.value?.officer_type === 'P'">
                {{ entry.value?.first_name || '' }}
                <template v-if="entry.value?.middle_name">
                  {{ entry.value?.middle_name }}
                </template>
                {{ entry.value?.last_name || '' }}
              </template>
              <template v-else>
                {{ entry.value?.name || 'Corporation' }}
              </template>
            </h4>
            <p class="agent-type text-muted mb-0">
              <i
                class="fa-light fa-user me-1"
                v-if="entry.value?.officer_type === 'P'"
              ></i>
              <i class="fa-light fa-building me-1" v-else></i>
              {{ getOfficerTypeName(entry.value?.officer_type) }}
              <span v-if="entry.value?.title" class="ms-2">
                <i class="fa-light fa-id-badge me-1"></i>
                {{ entry.value.title }}
              </span>
            </p>
          </div>

          <!-- Address Section -->
          <div class="address-section mb-4">
            <div class="section-header mb-2">
              <h6 class="text-muted mb-0">
                <i class="fa-light fa-location-dot me-2"></i>
                Address
              </h6>
            </div>
            <div>
              <template
                v-for="(item, itemIdx) in getOfficerDisplayData(entry.value)"
                :key="itemIdx"
              >
                <div v-if="item.label === 'Address'" class="mb-3">
                  <span v-if="item.isHtml" v-html="item.value"></span>
                  <span v-else>{{ item.value }}</span>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="d-flex gap-2 justify-content-start mt-4">
          <div class="d-grid col-md-3">
            <button
              class="btn btn-sm"
              type="button"
              :class="entry.editMode ? 'btn-primary' : 'btn-outline-primary'"
              @click="toggleEditMode(idx)"
            >
              <i
                class="fa-light"
                :class="entry.editMode ? 'fa-check' : 'fa-pen-to-square'"
              ></i>
              {{ entry.editMode ? 'Save' : 'Edit' }}
            </button>
          </div>
          <div v-if="entry.editMode" class="d-grid col-md-3">
            <button
              class="btn btn-outline-danger btn-sm"
              type="button"
              @click="remove(idx)"
            >
              <i class="fa-sharp fa-light fa-xmark"></i> Remove
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Button -->
    <button
      type="button"
      class="btn btn-outline-primary btn d-flex align-items-center justify-content-center gap-2"
      @click="addForm"
    >
      <i class="fa-light fa-user-plus"></i> Add Officer
    </button>
  </div>
</template>
